import type { DocumentResult, LineItemRow, OcrBox, PageResult } from '../shared/desktop-api';
import { lineItemEvidenceForCell } from '../shared/line-item-evidence';
import { createLineDraftContext, lineDraftCells, type LineDraftContext } from './line-item-drafts';
import { knownPixelSize } from './field-source-geometry';

export type RowSource =
  | { kind: 'missing'; reason: string }
  | {
      kind: 'source';
      scope: 'row' | 'shared';
      sharedWithRowNumbers: number[];
      page: PageResult;
      boxes: OcrBox[];
      lineIndexes: number[];
      identity: string;
    };
const missing = (reason = 'Row source not located'): RowSource => ({ kind: 'missing', reason });
const rowIdentity = (row: LineItemRow) =>
  JSON.stringify([
    row.rowNumber,
    row.description,
    row.quantity,
    row.unitPrice,
    row.amount,
    row.pageNumber,
    row.evidenceLineIndexes,
    row.cellEvidence,
    row.source,
    row.verified,
  ]);
const validBox = (box: OcrBox) =>
  [box.x, box.y, box.width, box.height].every(Number.isFinite) &&
  box.x >= 0 &&
  box.y >= 0 &&
  box.width > 0 &&
  box.height > 0;

/** A row overview is not a claim that every cell has a located source. Combine
 * only supplied row/cell citations; never search text or borrow adjacent rows. */
export function resolveRowSource(
  document: DocumentResult,
  row: LineItemRow,
  revision: string,
  tableIdentity: string,
  context?: LineDraftContext,
): RowSource {
  if (
    !document.id ||
    !revision ||
    !tableIdentity ||
    !Number.isInteger(row.rowNumber) ||
    row.rowNumber < 1
  )
    return missing();
  const projection = context?.document === document ? context : createLineDraftContext(document);
  if (revision !== projection.documentMutationToken || tableIdentity !== projection.tableIdentity)
    return missing('Row source changed');
  const owners = document.lineItems.filter((candidate) => candidate.rowNumber === row.rowNumber);
  if (owners.length !== 1 || rowIdentity(owners[0]) !== rowIdentity(row))
    return missing('Row source changed');
  if (document.notReadReason?.trim()) return missing('Row source was not read');

  const groups = [
    { pageNumber: row.pageNumber, lineIndexes: row.evidenceLineIndexes },
    ...lineDraftCells.map((cell) => lineItemEvidenceForCell(row, cell)),
  ];
  const indexes = new Set<number>();
  let pageNumber: number | undefined;
  for (const group of groups) {
    if (!Array.isArray(group.lineIndexes)) return missing('Row source is incomplete');
    if (!group.lineIndexes.length) continue;
    if (
      !Number.isInteger(group.pageNumber) ||
      group.pageNumber < 1 ||
      (pageNumber !== undefined && pageNumber !== group.pageNumber)
    )
      return missing('Row source spans different pages');
    if (
      group.lineIndexes.length > 128 ||
      new Set(group.lineIndexes).size !== group.lineIndexes.length
    )
      return missing('Row source is ambiguous');
    pageNumber = group.pageNumber;
    for (const index of group.lineIndexes) {
      if (!Number.isInteger(index) || index < 0) return missing('Row source lines are invalid');
      indexes.add(index);
    }
  }
  if (pageNumber === undefined || !indexes.size || indexes.size > 128) return missing();
  const pages = document.pages.filter((page) => page.number === pageNumber);
  if (pages.length !== 1) return missing('Source page is missing or ambiguous');
  const page = pages[0];
  if (!page.previewPath || page.notReadReason?.trim()) return missing('Row source was not read');

  // Shared citations cannot identify one row uniquely. They can still show the
  // original cited area as explicitly shared context, never as exact row proof.
  // Do not enlarge the crop with another row's uncited lines or infer a split.
  const sharedWithRowNumbers: number[] = [];
  for (const other of document.lineItems) {
    if (other === owners[0]) continue;
    const otherGroups = [
      { pageNumber: other.pageNumber, lineIndexes: other.evidenceLineIndexes },
      ...lineDraftCells.map((cell) => lineItemEvidenceForCell(other, cell)),
    ];
    if (
      otherGroups.some(
        (group) =>
          group.pageNumber === pageNumber &&
          Array.isArray(group.lineIndexes) &&
          group.lineIndexes.some((index) => indexes.has(index)),
      )
    ) {
      if (!Number.isInteger(other.rowNumber) || other.rowNumber < 1)
        return missing('Row source is ambiguous');
      sharedWithRowNumbers.push(other.rowNumber);
    }
  }
  sharedWithRowNumbers.sort((a, b) => a - b);
  const scope = sharedWithRowNumbers.length ? 'shared' : 'row';
  const lineIndexes = [...indexes].sort((a, b) => a - b);
  const boxes: OcrBox[] = [];
  for (const index of lineIndexes) {
    const line = page.lines[index],
      polygon = line?.polygon;
    if (
      !line ||
      !Array.isArray(polygon) ||
      polygon.length < 3 ||
      polygon.length > 32 ||
      polygon.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))
    )
      return missing('Row source area is invalid');
    const area = polygon.reduce((sum, point, i) => {
      const next = polygon[(i + 1) % polygon.length];
      return sum + point.x * next.y - next.x * point.y;
    }, 0);
    const x = Math.min(...polygon.map((point) => point.x)),
      y = Math.min(...polygon.map((point) => point.y));
    const box = {
      x,
      y,
      width: Math.max(...polygon.map((point) => point.x)) - x,
      height: Math.max(...polygon.map((point) => point.y)) - y,
    };
    if (!Number.isFinite(area) || area === 0 || !validBox(box))
      return missing('Row source area is invalid');
    boxes.push(box);
  }
  const identity = JSON.stringify([
    document.id,
    revision,
    tableIdentity,
    rowIdentity(row),
    scope,
    sharedWithRowNumbers,
    page.number,
    page.previewPath,
    page.sourcePath,
    page.sourcePageNumber,
    page.sourceOccurrenceId,
    page.pixelWidth,
    page.pixelHeight,
    lineIndexes.map((index) => page.lines[index]),
  ]);
  const result: RowSource = {
    kind: 'source',
    scope,
    sharedWithRowNumbers,
    page,
    boxes,
    lineIndexes,
    identity,
  };
  const stored = knownPixelSize(page);
  if (stored && !rowSourceCropBounds(result, stored.width, stored.height))
    return missing('Row source area is outside the page');
  return result;
}

/** Preserve a whole-width band of ORIGINAL paper. A description-only citation
 * must not crop away uncited numeric columns or falsely align printed cells. */
export function rowSourceCropBounds(
  source: Extract<RowSource, { kind: 'source' }>,
  width: number,
  height: number,
): OcrBox | null {
  const stored = knownPixelSize(source.page);
  if (
    ![width, height].every((value) => Number.isFinite(value) && value > 0) ||
    (stored && (stored.width !== width || stored.height !== height)) ||
    !source.boxes.length ||
    source.boxes.some(
      (box) => !validBox(box) || box.x + box.width > width || box.y + box.height > height,
    )
  )
    return null;
  const top = Math.min(...source.boxes.map((box) => box.y));
  const bottom = Math.max(...source.boxes.map((box) => box.y + box.height));
  const margin = Math.max(12, Math.min(48, (bottom - top) * 0.65));
  const y = Math.max(0, top - margin);
  return { x: 0, y, width, height: Math.min(height, bottom + margin) - y };
}
